import { initializeApp, FirebaseApp, getApps, getApp } from "firebase/app";
import {
    getDatabase,
    ref,
    onChildAdded,
    onChildChanged,
    onValue,
    set,
    get,
    serverTimestamp,
    runTransaction,
} from "firebase/database";
import _isEmpty from "lodash/isEmpty";
import { RSQueueConnection } from "../../../models";

let app: FirebaseApp;

interface FirebaseQueueConfig {
    disabled: boolean;
    insideTimeoutMinutes: number;
    maxPeopleInside: number;
    outsideTimeoutMinutes: number;
}

interface FirebaseQueueData {
    inside?: number;
    lastHeartBeat: number;
    sequenceNumber: number;
    startedwaiting: number;
}

export async function init(checkoutQueueConnection: RSQueueConnection): Promise<void> {
    if (app?.name === "queueApp") {
        return;
    } else if (getApps().some((item) => item.name === "queueApp")) {
        app = getApp("queueApp");
    } else {
        app = initializeApp(checkoutQueueConnection, "queueApp");
    }
}

export async function waitInQueue(
    orderRef: string,
    checkoutQueueConnection: RSQueueConnection,
    {
        onIsInside,
        onIsLoading,
        onIsPutInQueue,
        onPlaceInQueueUpdate,
    }: {
        onIsInside: (isInside: boolean) => void;
        onIsLoading: (value: boolean) => void;
        onIsPutInQueue: () => void;
        onPlaceInQueueUpdate: ({
            placeInQueue,
            timeLeftInQueueMins,
            maxPeopleInside,
        }: {
            maxPeopleInside: number;
            placeInQueue: number;
            timeLeftInQueueMins: number;
        }) => void;
    },
): Promise<void> {
    await init(checkoutQueueConnection);
    const database = getDatabase(app);
    const queueConfigSnapshot = await get(ref(database, "config"));
    const queueConfig: FirebaseQueueConfig = queueConfigSnapshot.val();
    if (queueConfig.disabled) {
        onIsInside(true);
        return;
    }

    let maxPeopleInside = queueConfig.maxPeopleInside;

    onChildChanged(ref(database, "config"), (snapshot) => {
        if ((snapshot.key || "") === "disabled" && snapshot.val()) {
            onIsInside(true);
        }
        if (snapshot.key === "maxPeopleInside" && snapshot.val()) {
            maxPeopleInside = snapshot.val();
        }
    });

    onIsLoading(false);

    const sequenceNumber = await setStartedWaitingFirstTime(orderRef);
    onIsPutInQueue();

    onValue(ref(database, "firstWaitingSequenceNumber"), (firstWaitingSequenceNumber) => {
        const placeInQueue = Math.max(1, sequenceNumber - firstWaitingSequenceNumber.val() + 1);
        const guesstimatedAverageCheckoutMins = 3;
        const usersThroughPerMin = guesstimatedAverageCheckoutMins / maxPeopleInside;
        const timeLeftInQueueMins = Math.ceil(usersThroughPerMin * placeInQueue);
        onPlaceInQueueUpdate({ placeInQueue, timeLeftInQueueMins, maxPeopleInside });
    });

    onChildAdded(ref(database, "queue/" + orderRef), (snapshot) => {
        if (snapshot.key === "inside") {
            onIsInside(true);
        }
    });
}

export async function updateHeartBeat(orderRef: string): Promise<void> {
    const database = getDatabase(app);
    const snapshot = await get(ref(database, "queue/" + orderRef));
    const data: FirebaseQueueData = snapshot.val();
    if (!_isEmpty(data)) {
        await set(ref(database, "queue/" + orderRef), { ...data, lastHeartBeat: serverTimestamp() });
    }
}

/**
 * Create a new queue entry.
 * @param {string} orderRef
 * @returns {Promise.<number>} a promise of the new entry's sequenceNumber
 */
async function setStartedWaitingFirstTime(orderRef: string): Promise<number> {
    const database = getDatabase(app);
    const queueSnapshot = await get(ref(database, "queue/" + orderRef));

    if (queueSnapshot.exists()) {
        return queueSnapshot.val().sequenceNumber;
    }

    // Increase the sequence number by one, return the new value
    const { snapshot } = await runTransaction(ref(database, "lastSequenceNumber"), (v) => v + 1);
    const sequenceNumber: number = snapshot.val();
    await set(ref(database, "queue/" + orderRef), {
        startedwaiting: serverTimestamp(),
        sequenceNumber,
    });
    return sequenceNumber;
}
